import {Box, Button, Flex, Heading, Text} from "@chakra-ui/react"
import {Link as RouterLink, Navigate} from "react-router-dom"
import {useTranslation} from "react-i18next"
import {Form, Formik} from "formik"
import {object, string} from "yup"
import {toaster} from "../components/ui/toaster"
import {useAppSelector} from "../hooks/store"
import {isBarberLoggedIn} from "../slices/authSlice"
import {LoginPayload, useLoginMutation} from "../services/authService"
import {analytics} from "../utils/analytics"
import AuthShell from "../components/Auth/AuthShell"
import AuthField from "../components/Auth/AuthField"
import BackLink from "../components/Auth/BackLink"
import {FiArrowRight} from "react-icons/fi"

export default function Login() {
    const {t} = useTranslation()
    const [login, {isLoading}] = useLoginMutation()
    const isLogged = useAppSelector(isBarberLoggedIn)

    const schema = object({
        username: string().email(t('validation.invalidEmail')).required(t('validation.required')),
        password: string().min(2).max(50).required(t('validation.required')),
    })

    if (isLogged) return <Navigate to="/dashboard"/>

    return (
        <AuthShell narrow>
            <Box mb="28px">
                <BackLink to="/login" label={t('auth.back')}/>
                <Text
                    fontFamily="mono"
                    fontSize="11px"
                    letterSpacing="0.2em"
                    textTransform="uppercase"
                    color="#0088FF"
                    fontWeight={700}
                    mt="18px"
                >
                    {t('auth.login.barber.eyebrow')}
                </Text>
                <Heading
                    as="h1"
                    fontFamily="heading"
                    fontWeight={700}
                    fontSize="28px"
                    color="#171717"
                    mt="8px"
                    letterSpacing="-0.01em"
                >
                    {t('auth.login.title')}
                </Heading>
            </Box>

            <Formik<LoginPayload>
                validationSchema={schema}
                validateOnMount
                onSubmit={(values, {setSubmitting}) => {
                    setSubmitting(true)
                    login(values).unwrap()
                        .then(() => {
                            analytics.loginSucceeded('barber')
                            toaster.create({
                                title: t('customerAuth.loginSuccess'),
                                type: "success",
                                duration: 3000,
                                closable: true,
                            })
                        })
                        .catch((error) => {
                            const reason = error?.status === 401 ? 'invalid_credentials'
                                : error?.status >= 500 ? 'server_error' : 'unknown'
                            analytics.loginFailed('barber', reason)
                            toaster.create({
                                title: t('customerAuth.loginError'),
                                description: t('booking.tryAgain'),
                                type: "error",
                                duration: 3000,
                                closable: true,
                            })
                        })
                    setSubmitting(false)
                }}
                initialValues={import.meta.env.DEV
                    ? {username: 'barber@martive.sk', password: 'password'}
                    : {username: '', password: ''}}
            >
                {({isSubmitting}) => (
                    <Form>
                        <AuthField
                            name="username"
                            type="email"
                            label={t('common.email')}
                            placeholder="you@example.com"
                            autoComplete="email"
                            required
                        />
                        <AuthField
                            name="password"
                            type="password"
                            label={t('login.password')}
                            placeholder="••••••••"
                            autoComplete="current-password"
                            required
                        />

                        <Flex justifyContent="flex-end" mt="-8px" mb="20px">
                            <RouterLink to="/forgot-password" style={{color: "#0088FF", fontSize: 13, fontWeight: 600}}>
                                {t('auth.forgotPassword')}
                            </RouterLink>
                        </Flex>

                        <Button
                            type="submit"
                            loading={isLoading || isSubmitting}
                            w="full"
                            h="46px"
                            bg="#0088FF"
                            color="white"
                            borderRadius="12px"
                            fontFamily="mono"
                            fontWeight={700}
                            fontSize="12px"
                            letterSpacing="0.14em"
                            textTransform="uppercase"
                            boxShadow="0 4px 12px rgba(0,136,255,.30)"
                            _hover={{
                                bg: "#0088FF",
                                transform: "translateY(-2px)",
                                boxShadow: "0 8px 24px rgba(0,136,255,.40)",
                            }}
                        >
                            {t('login.submitButton')} <FiArrowRight size={14} style={{marginLeft: 8}}/>
                        </Button>

                        {/* HIDDEN_TODO #32 — Google login hidden: the button was a
                            permanently-disabled dead control (no OAuth flow wired).
                            Bring back with real Google OAuth. See docs/HIDDEN_TODO.md.
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            my="20px"
                            fontFamily="mono"
                            fontSize="13px"
                            color="#A3A3A3"
                            letterSpacing="0.14em"
                        >
                            {t('auth.or')}
                        </Flex>

                        <Button
                            w="full"
                            h="46px"
                            bg="transparent"
                            color="#404040"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            borderRadius="12px"
                            fontFamily="mono"
                            fontWeight={700}
                            fontSize="12px"
                            letterSpacing="0.14em"
                            textTransform="uppercase"
                            _hover={{bg: "#F5F5F5", borderColor: "#D4D4D4"}}
                            disabled
                        >
                            {t('auth.continueWithGoogle')}
                        </Button>
                        */}

                        <Flex justifyContent="center" mt="24px">
                            <Text fontSize="14px" color="#737373">
                                {t('auth.login.noAccount')}{' '}
                                <RouterLink to="/register/barber" style={{color: "#0088FF", fontWeight: 600}}>
                                    {t('auth.login.signUp')}
                                </RouterLink>
                            </Text>
                        </Flex>
                    </Form>
                )}
            </Formik>
        </AuthShell>
    )
}
